import { useSubjectMember } from '@healthblocks-io/core'
import { isNil } from 'ramda'
import useSWR from 'swr'

import { PatientCommunicationStatus } from '@/modules/care-question/hooks/usePatientCommunication'
import { useIntegrationAxios } from '@/modules/healthblocks/hooks/useIntegrationAxios'
import { getLatestMessageFromList, getLatestNonPatientSenderFromList } from '../utils/messageOrder'

import type {
  PatientCommunicationChannels,
  PatientCommunicationTarget,
} from '@/modules/care-question/types/patient-communication.type'
import type { PatientCommunication } from '../types/message.types'

export function useReplyStatusPatientCommunication(patientCommunication?: PatientCommunication) {
  const axios = useIntegrationAxios()
  const member = useSubjectMember()

  const checkCanReplyFetcher = async (): Promise<PatientCommunicationStatus> => {
    // Step 1: Check if the last message was sent by the patient (by createdAt)
    const latestMessage = getLatestMessageFromList(patientCommunication?.messages)
    if (latestMessage?.sender?.type === 'patient') {
      return PatientCommunicationStatus.AWAITING_REPLY
    }

    // Step 2: Get allowed channels and doctors from Synops
    const allowedChannelsConfig = await axios
      .get<PatientCommunicationChannels>('/synops/get-communication-channels', {
        params: { synopsPatientId: member?.id },
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Something went wrong while fetching the channels', error)

        return {
          patientId: member?.id,
          channels: [],
        } as PatientCommunicationChannels
      })

    // Step 3: Check if the question channel is allowed
    const questionChannelConfig = allowedChannelsConfig?.channels.find((channel) => channel.type === 'question')

    const latestSender = getLatestNonPatientSenderFromList(patientCommunication?.messages) as
      | (PatientCommunicationTarget & {
          specialisationId?: number
          userId?: number
        })
      | undefined

    // Step 4: If sender is not a synops user check if patient has the question channel
    if (latestSender?.type !== 'patient' && latestSender?.specialisationId === undefined) {
      return isNil(questionChannelConfig) ? PatientCommunicationStatus.RESTRICTED : PatientCommunicationStatus.ALLOWED
    }

    // Step 4: Check if the specialisation is in the question channel
    const specialisation = questionChannelConfig?.specialisations.find(
      (spec) => spec.id === (latestSender?.specialisationId as number),
    )

    // Specialisation not found, can't reply
    if (!specialisation) {
      return PatientCommunicationStatus.RESTRICTED
    }

    // Step 5: Check if the specialisation has a preferred doctor
    if (latestSender?.type !== 'patient' && latestSender?.userId === undefined) {
      return PatientCommunicationStatus.ALLOWED
    }

    // Step 6: Check if the doctor is in the specialisation
    const doctor = specialisation.doctors?.find((doctor) => doctor.id === (latestSender?.userId as number))
    // Doctor found = allow reply, else don't
    return isNil(doctor) ? PatientCommunicationStatus.RESTRICTED : PatientCommunicationStatus.ALLOWED
  }

  return useSWR(`check-can-reply-patient-communication-${patientCommunication?.id}`, checkCanReplyFetcher, {
    errorRetryCount: 3,
  })
}
